import {
  ChecklistMetricCriteria,
  ChecklistMetricStatus,
  MultiLocationResult,
} from '../../../handoff-checklist-v2/handoff-checklist-v2.types';
import { deriveOverallStatus } from '../../../handoff-checklist-v2/handoff-checklist-v2.helpers';
import { MetricResult, Result } from '../../../handoff-metrics/handoff-metrics.types';
import { UnifyMetricResult } from '../../unify-metrics.types';
import { UNIFY_SECTION_NAMES } from '../../unify-metric-definitions';

export interface MetricSectionGroup {
  sectionName: string;
  metrics: UnifyMetricResult[];
}

/**
 * Applies user-entered exceptions onto raw multi-location metric results,
 * recalculating individual criteria statuses and rolling them up into
 * each metric's overall status.
 */
export const applyExceptionsToMetrics = (
  rawMetrics: UnifyMetricResult[],
  exceptions: Record<string, string>
): UnifyMetricResult[] => {
  return rawMetrics.map((metric) => {
    const locationResults: MultiLocationResult[] = (metric.locationResults || []).map(
      (lr) => {
        const criterias: ChecklistMetricCriteria[] = lr.criterias.map(
          (c, criteriaIndex) => {
            const key = `${lr.locationId}:${metric.id}:${criteriaIndex}`;
            const userException = exceptions[key];
            const exception =
              userException !== undefined ? userException : c.exception || '';

            // Do not override pending criteria while evaluation is in-flight
            if (c.status === ChecklistMetricStatus.PENDING) {
              return { ...c, exception };
            }

            const isPassed = c.status === ChecklistMetricStatus.PASSED;
            let status = c.status;
            if (!isPassed) {
              const allowsException =
                c.exceptionType !== undefined && c.exceptionType !== 'none';
              status =
                allowsException && exception.trim() !== ''
                  ? ChecklistMetricStatus.EXCEPTION_NOTED
                  : ChecklistMetricStatus.BLOCKED;
            }

            return {
              ...c,
              exception,
              status,
            };
          }
        );

        return {
          ...lr,
          criterias,
        };
      }
    );

    const allStatuses = locationResults.flatMap((lr) =>
      lr.criterias.map((c) => c.status)
    );

    let status = metric.status;
    if (metric.isLoading || allStatuses.includes(ChecklistMetricStatus.PENDING)) {
      status = ChecklistMetricStatus.PENDING;
    } else if (allStatuses.length === 0) {
      status = ChecklistMetricStatus.PASSED;
    } else {
      status = deriveOverallStatus(allStatuses);
    }

    return {
      ...metric,
      status,
      locationResults,
    };
  });
};

/**
 * Splits metrics into processing, attention, and completed states.
 */
export const splitMetricsByAttention = (
  metrics: UnifyMetricResult[]
): {
  processing: UnifyMetricResult[];
  needsAttention: UnifyMetricResult[];
  completed: UnifyMetricResult[];
} => {
  const processing: UnifyMetricResult[] = [];
  const needsAttention: UnifyMetricResult[] = [];
  const completed: UnifyMetricResult[] = [];

  metrics.forEach((metric) => {
    if (metric.isLoading || metric.status === ChecklistMetricStatus.PENDING) {
      processing.push(metric);
    } else if (metric.status === ChecklistMetricStatus.PASSED) {
      completed.push(metric);
    } else {
      needsAttention.push(metric);
    }
  });

  return { processing, needsAttention, completed };
};

/**
 * Groups metrics by sectionName preserving definition order from UNIFY_SECTION_NAMES.
 */
export const groupMetricsBySection = (
  metrics: UnifyMetricResult[]
): MetricSectionGroup[] => {
  const map = new Map<string, UnifyMetricResult[]>();
  metrics.forEach((m) => {
    const list = map.get(m.sectionName) ?? [];
    list.push(m);
    map.set(m.sectionName, list);
  });

  const groups: MetricSectionGroup[] = [];
  UNIFY_SECTION_NAMES.forEach((sectionName) => {
    const sectionMetrics = map.get(sectionName);
    if (sectionMetrics && sectionMetrics.length > 0) {
      groups.push({ sectionName, metrics: sectionMetrics });
      map.delete(sectionName);
    }
  });

  // Any remaining sections not defined in UNIFY_SECTION_NAMES
  map.forEach((sectionMetrics, sectionName) => {
    if (sectionMetrics.length > 0) {
      groups.push({ sectionName, metrics: sectionMetrics });
    }
  });

  return groups;
};

/**
 * Builds per-location MetricResult[] ready for snapshot persistence upon submit.
 */
export const buildResultsByLocation = (
  metrics: UnifyMetricResult[],
  selectedLocations: { locationId: string }[]
): Record<string, MetricResult[]> => {
  const map: Record<string, MetricResult[]> = {};
  selectedLocations.forEach((loc) => {
    map[loc.locationId] = [];
  });

  metrics.forEach((metric) => {
    const locResults = metric.locationResults || [];
    locResults.forEach((lr) => {
      if (!map[lr.locationId]) {
        map[lr.locationId] = [];
      }
      lr.criterias.forEach((c) => {
        map[lr.locationId].push({
          section: metric.sectionName,
          subsection: metric.name,
          testCriteria: c.description,
          expectedValue: String(c.expectedValue ?? ''),
          actualValue: String(c.actualValue ?? ''),
          result: c.status === ChecklistMetricStatus.PASSED ? Result.Pass : Result.Fail,
          exception: c.exception || '',
        });
      });
    });
  });

  return map;
};

/**
 * Submission requires:
 * - not actively evaluating
 * - at least 1 selected location
 * - metrics array is populated
 * - every metric is PASSED or has an EXCEPTION_NOTED (no BLOCKED or PENDING)
 */
export const canSubmitMetrics = (
  metrics: UnifyMetricResult[],
  isEvaluating: boolean,
  selectedLocationsCount: number
): boolean => {
  if (isEvaluating || selectedLocationsCount === 0 || metrics.length === 0) {
    return false;
  }
  return metrics.every(
    (m) =>
      m.status === ChecklistMetricStatus.PASSED ||
      m.status === ChecklistMetricStatus.EXCEPTION_NOTED
  );
};
